/**
 * Transformr brand theme — Fluent UI v9 light theme.
 *
 * Single source of truth for the Readiness app's design system. Centred on the
 * Transformr green ramp (#62B73B = primary brand) per the agreed brand pivot
 * (green primary, blue/navy + accents as the supporting palette). Typography,
 * the full brand palette, and chrome tokens are aligned to the Transformr
 * "executive briefing" system used by DevOpsReports and the report templates:
 *   - colours mirror the HTML `--tr-*` design tokens (= DevOpsReports brandColors)
 *   - fonts: Plus Jakarta Sans (headings), Inter (body), JetBrains Mono (mono)
 *   - chrome: soft card shadows, 8px radius, hairline borders
 *
 * This file lives under src/theme/ and is exempt from lint:ux, so literal hex
 * and px values are allowed here (and ONLY here) — components must consume these
 * tokens, never re-declare raw values.
 */

import {
  createLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

// Transformr green brand ramp built around #62B73B (the official brand green).
export const transformrGreen: BrandVariants = {
  10: '#060D02',
  20: '#162B08',
  30: '#224210',
  40: '#2E5918',
  50: '#3A7020',
  60: '#478829',
  70: '#549F32',
  80: '#62B73B',
  90: '#79C44E',
  100: '#8FD062',
  110: '#A1D87A',
  120: '#B3E092',
  130: '#C3E7A9',
  140: '#D3EEC0',
  150: '#E2F4D7',
  160: '#F0F9ED',
};

// ─────────────────────────────────────────────────────────────────────────────
// Brand fonts — the Transformr "executive briefing" type system, loaded via the
// Google Fonts <link> in index.html (mirrors DevOpsReports brandFonts).
// ─────────────────────────────────────────────────────────────────────────────
export const brandFonts = {
  heading: "'Plus Jakarta Sans', 'Segoe UI', system-ui, sans-serif",
  body: "'Inter', 'Segoe UI', system-ui, sans-serif",
  mono: "'JetBrains Mono', 'Cascadia Code', Consolas, monospace",
} as const;

/**
 * Live Fluent theme. Green brand ramp + Transformr typography (Inter body,
 * JetBrains mono). Headings (Fluent Title/Subtitle) get Plus Jakarta Sans via
 * the global rule in index.css.
 */
export const discoveryTheme: Theme = {
  ...createLightTheme(transformrGreen),
  fontFamilyBase: brandFonts.body,
  fontFamilyMonospace: brandFonts.mono,
  fontFamilyNumeric: brandFonts.body,
};

/**
 * Hex constants for places where a Fluent token cannot be used (inline
 * gradients, badge fills, chart series, decorative accents). Values mirror the
 * HTML `--tr-*` tokens / DevOpsReports brandColors so the products read as one
 * family. Existing green-* keys are preserved for backwards compatibility.
 */
export const transformrBrand = {
  // Greens — #62B73B is the primary brand; #95C43E is the briefing accent green.
  green: '#62B73B',
  greenAlt: '#95C43E', // --tr-green / --rdy-accent
  greenDark: '#579F34',
  greenDeep: '#3A7020',
  greenLight: '#79C44E',
  greenPale: '#B3E092',
  greenInk: '#4A6620', // --rdy-accent-ink (text on soft-green)
  greenSoft: 'rgba(149, 196, 62, 0.16)', // --rdy-accent-soft (tint fills)

  // Blues / navy — supporting palette.
  blue: '#0066B2', // --tr-blue
  blueDeep: '#1A3A5C',
  blueLight: '#56ADDF', // --tr-blue-light
  blueDark: '#2E3191', // --tr-blue-dark (gradient/admin)
  navy: '#011338', // --tr-navy (deepest brand tone / hero)

  // Accents.
  orange: '#D9782D', // --tr-orange
  purple: '#9E1F63', // --tr-purple
  yellow: '#F1B934', // --tr-yellow

  // Greys & text.
  grey100: '#F4F4F4',
  grey200: '#E8E8E8',
  grey300: '#D9D9D9',
  grey700: '#6B6B6B',
  white: '#FFFFFF',
  textDark: '#424242', // --tr-text
  textMuted: '#6B6B6B',
  textSubtle: '#444444',
} as const;

/**
 * Chrome tokens — soft elevation, radius, hairline borders and status colours
 * matching the Transformr briefing system (DevOpsReports teams-theme chrome).
 * Use for cards, panels, focus rings and status dots so the whole app shares
 * one structural language.
 */
export const chromeTokens = {
  radiusCard: '12px',
  radiusControl: '8px',
  borderHairline: `1px solid ${transformrBrand.grey200}`,
  shadowCard:
    '0 0.3px 0.9px rgba(0, 0, 0, 0.06), 0 1.6px 3.6px rgba(0, 0, 0, 0.08)',
  shadowCardHover:
    '0 2px 8px rgba(0, 0, 0, 0.08), 0 0.6px 1.8px rgba(0, 0, 0, 0.06)',
  shadowElevated:
    '0 4px 12px rgba(0, 0, 0, 0.10), 0 1.2px 3.6px rgba(0, 0, 0, 0.08)',
  shadowDialog:
    '0 20px 50px rgba(0, 0, 0, 0.15), 0 4.8px 14.4px rgba(0, 0, 0, 0.12)',
  // Status palette (matches HTML --st-* tokens).
  statusActive: transformrBrand.greenAlt, // #95C43E
  statusNew: transformrBrand.blueLight, // #56ADDF
  statusClosed: transformrBrand.blueDark, // #2E3191
  statusResolved: transformrBrand.purple, // #9E1F63
  statusRemoved: transformrBrand.grey700, // #6B6B6B
  statusWarning: transformrBrand.yellow,
} as const;

export type ChromeTokens = typeof chromeTokens;

/**
 * Sidebar (LHS app-shell) tokens — the DevOpsReports TeamsAppLayout design: a
 * dark Transformr navy surface with white nav text (matches the other apps).
 * The active state uses the green brand accent (the rest of the app stays
 * primary green) rather than DevOpsReports' blue.
 */
export const sidebarTokens = {
  surface: transformrBrand.navy, // #011338 navy sidebar
  text: 'rgba(255, 255, 255, 0.75)',
  textStrong: '#FFFFFF',
  muted: 'rgba(255, 255, 255, 0.55)',
  hover: 'rgba(255, 255, 255, 0.06)',
  active: 'rgba(149, 196, 62, 0.22)', // green accent @22% on navy
  border: 'rgba(255, 255, 255, 0.08)',
} as const;

export type SidebarTokens = typeof sidebarTokens;
